import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import PatientNavigationMenu from '../../components/ui/PatientNavigationMenu';
import WelcomeHeader from './components/WelcomeHeader';
import QuickActionCard from './components/QuickActionCard';
import SymptomLogModal from './components/SymptomLogModal';
import ProgressChart from './components/ProgressChart';
import RecentActivityFeed from './components/RecentActivityFeed';
import StatsOverview from './components/StatsOverview';
import FloatingActionButton from './components/FloatingActionButton';

const PatientDashboard = () => {
  const [isSymptomModalOpen, setIsSymptomModalOpen] = useState(false);
  const [patientData, setPatientData] = useState({
    name: 'Sarah Johnson',
    lastLogin: '2025-08-24',
    stats: {
      totalSymptomLogs: 28,
      averageSeverity: 4.1,
      gamesPlayed: 22,
      averageGameScore: 89,
      streakDays: 12,
      lastLogDate: '2025-08-24'
    }
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    // Simulate loading patient data from Firestore
    const loadPatientData = () => {
      // Mock recent activities
      const mockActivities = [
        {
          id: 1,
          type: 'symptom',
          title: 'Logged Symptoms',
          description: 'Headache, Fatigue - Severity: 5/10',
          timestamp: '2025-08-24T11:30:00',
          icon: 'AlertCircle',
          color: 'text-warning'
        },
        {
          id: 2,
          type: 'game',
          title: 'Reaction Time Test',
          description: 'Score: 91% - Excellent performance!',
          timestamp: '2025-08-24T10:15:00',
          icon: 'Gamepad2',
          color: 'text-success'
        },
        {
          id: 3,
          type: 'symptom',
          title: 'Logged Symptoms',
          description: 'Memory Issues - Severity: 3/10',
          timestamp: '2025-08-23T18:45:00',
          icon: 'AlertCircle',
          color: 'text-success'
        }
      ];
      setRecentActivities(mockActivities);
    };

    loadPatientData();
  }, []);

  const handleSymptomLog = () => {
    setIsSymptomModalOpen(true);
  };

  const handleSymptomSave = (logData) => {
    // Simulate saving to Firestore
    console.log('Saving symptom log:', logData);
    
    // Add to recent activities
    const newActivity = {
      id: Date.now(),
      type: 'symptom',
      title: 'Logged Symptoms',
      description: `${logData?.symptoms?.join(', ')} - Severity: ${logData?.severity}/10`,
      timestamp: logData?.timestamp,
      icon: 'AlertCircle',
      color: logData?.severity <= 4 ? 'text-success' : logData?.severity <= 6 ? 'text-warning' : 'text-error'
    };
    
    setRecentActivities(prev => [newActivity, ...prev?.slice(0, 4)]);
    
    // Update stats
    setPatientData(prev => ({
      ...prev,
      stats: {
        ...prev?.stats,
        totalSymptomLogs: prev?.stats?.totalSymptomLogs + 1,
        lastLogDate: new Date()?.toLocaleDateString()
      }
    }));
  };

  const handleCognitiveGames = () => {
    navigate('/cognitive-games-section');
  };

  const handleViewProgress = () => {
    // Navigate to detailed progress view (would be implemented)
    console.log('Navigate to detailed progress view');
  };

  const handleLogout = () => {
    navigate('/login-screen');
  };

  const handleProfileClick = () => {
    console.log('Navigate to profile settings');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <RoleBasedHeader
        userRole="patient"
        userName={patientData?.name}
        onLogout={handleLogout}
        onProfileClick={handleProfileClick}
      />
      {/* Main Content */}
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Navigation Menu */}
          <div className="mb-6">
            <PatientNavigationMenu
              onSymptomLogClick={handleSymptomLog}
            />
          </div>

          {/* Welcome Header */}
          <WelcomeHeader
            patientName={patientData?.name}
            lastLogin={patientData?.lastLogin}
          />

          {/* Stats Overview */}
          <StatsOverview stats={patientData?.stats} className="mb-6" />

          {/* Quick Action Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            <QuickActionCard
              title="Log Symptoms"
              description="Record your daily symptoms and track severity levels"
              icon="Plus"
              buttonText="Log Now"
              variant="primary"
              onClick={handleSymptomLog}
            />
            
            <QuickActionCard
              title="Cognitive Games"
              description="Play brain training games and track your performance"
              icon="Gamepad2"
              buttonText="Play Games"
              variant="accent"
              onClick={handleCognitiveGames}
            />
            
            <QuickActionCard
              title="View Progress"
              description="Analyze your health trends and cognitive performance"
              icon="TrendingUp"
              buttonText="View Details"
              variant="default"
              onClick={handleViewProgress}
            />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Progress Chart */}
            <div className="lg:col-span-2">
              <ProgressChart
                title="Health & Performance Trends"
                className="h-full"
              />
            </div>

            {/* Recent Activity Feed */}
            <div className="lg:col-span-1">
              <RecentActivityFeed
                activities={recentActivities}
                className="h-full"
              />
            </div>
          </div>
        </div>
      </div>
      {/* Floating Action Button */}
      <FloatingActionButton onClick={handleSymptomLog} />
      {/* Symptom Log Modal */}
      <SymptomLogModal
        isOpen={isSymptomModalOpen}
        onClose={() => setIsSymptomModalOpen(false)}
        onSave={handleSymptomSave}
      />
    </div>
  );
};

export default PatientDashboard;